/**
 * BET-85 — Canonical workflow descriptor for ChainWork workflows.
 *
 * The MCP runtime still consumes `WorkflowDefinition`, but built-in workflows
 * are authored as `WorkflowDescriptor` objects and compiled to the runtime
 * shape. The descriptor is the authoring contract; the compiled definition is a
 * backward-compatible runtime view for existing MCP surfaces.
 */

export type WorkflowOutputFormat =
  | "freetext"
  | "list"
  | "choice"
  | "structured";

export interface WorkflowRound {
  id: string;
  num: string;
  label: string;
  type: "open" | "choice" | "synthesis" | "commit" | "close";
  instruction: string;
  facilitatorGuidance: string;
  questions?: WorkflowQuestion[];
  outputSchema: {
    field: string;
    description: string;
    format: WorkflowOutputFormat;
  };
  kbCollection?: string;
  maxDurationHint?: string;
}

export interface WorkflowQuestion {
  id: string;
  prompt: string;
  options?: { id: string; label: string }[];
  allowMultiple?: boolean;
  required?: boolean;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  shortDescription: string;
  icon: string;
  facilitatorPreamble: string;
  rounds: WorkflowRound[];
  kbOutputCollection?: string;
  kbOutputTemplate?: {
    nameTemplate: string;
    descriptionField: string;
  };
  errorRecovery: string;
}

export type WorkflowPrimaryRecordRouting =
  | { mode: "fixed"; collection: string }
  | { mode: "classifier" };

export type WorkflowSummaryDescriptionSource =
  | "final-output-text"
  | { mode: "step-output-field"; field: string };

export interface WorkflowSummaryCapture {
  createsPrimaryRecord: boolean;
  nameTemplate: string;
  descriptionField?: string;
  descriptionSource?: WorkflowSummaryDescriptionSource;
}

export interface WorkflowEmittedArtifact {
  kind: "entry" | "update";
  collection?: string;
  description: string;
}

export interface WorkflowDescriptor {
  id: string;
  name: string;
  shortDescription: string;
  icon: string;
  facilitatorPreamble: string;
  rounds: WorkflowRound[];
  template: {
    slug: string;
    name: string;
  };
  output: {
    primaryRecord: {
      routing: WorkflowPrimaryRecordRouting;
    };
    summaryCapture?: WorkflowSummaryCapture;
    emits?: WorkflowEmittedArtifact[];
  };
  runtime: {
    kind: "guided" | "facilitated";
    primaryTool?: string;
  };
  errorRecovery: string;
}

function cloneWorkflowQuestion(question: WorkflowQuestion): WorkflowQuestion {
  return {
    ...question,
    options: question.options?.map((option) => ({ ...option })),
  };
}

function cloneWorkflowRound(round: WorkflowRound): WorkflowRound {
  return {
    ...round,
    questions: round.questions?.map(cloneWorkflowQuestion),
    outputSchema: { ...round.outputSchema },
  };
}

export function cloneWorkflowDescriptor(
  descriptor: WorkflowDescriptor,
): WorkflowDescriptor {
  return {
    ...descriptor,
    rounds: descriptor.rounds.map(cloneWorkflowRound),
    template: { ...descriptor.template },
    output: {
      primaryRecord: {
        routing:
          descriptor.output.primaryRecord.routing.mode === "fixed"
            ? {
                mode: "fixed",
                collection: descriptor.output.primaryRecord.routing.collection,
              }
            : { mode: "classifier" },
      },
      summaryCapture: descriptor.output.summaryCapture
        ? { ...descriptor.output.summaryCapture }
        : undefined,
      emits: descriptor.output.emits?.map((emitted) => ({ ...emitted })),
    },
    runtime: { ...descriptor.runtime },
  };
}

export function cloneWorkflowDefinition(
  definition: WorkflowDefinition,
): WorkflowDefinition {
  return {
    ...definition,
    rounds: definition.rounds.map(cloneWorkflowRound),
    kbOutputTemplate: definition.kbOutputTemplate
      ? { ...definition.kbOutputTemplate }
      : undefined,
  };
}

export function validateWorkflowDescriptor(
  descriptor: WorkflowDescriptor,
): void {
  if (descriptor.rounds.length === 0) {
    throw new Error(`Workflow '${descriptor.id}' must define at least one round.`);
  }

  const roundIds = new Set<string>();
  for (const [index, round] of descriptor.rounds.entries()) {
    if (roundIds.has(round.id)) {
      throw new Error(`Workflow '${descriptor.id}' has duplicate round id '${round.id}'.`);
    }
    roundIds.add(round.id);

    const expectedNum = String(index + 1).padStart(2, "0");
    if (round.num !== expectedNum) {
      throw new Error(
        `Workflow '${descriptor.id}' round '${round.id}' has num '${round.num}', expected '${expectedNum}'.`,
      );
    }

    const questionIds = new Set<string>();
    for (const question of round.questions ?? []) {
      if (questionIds.has(question.id)) {
        throw new Error(
          `Workflow '${descriptor.id}' round '${round.id}' has duplicate question id '${question.id}'.`,
        );
      }
      questionIds.add(question.id);
    }
  }

  if (
    descriptor.runtime.kind === "facilitated" &&
    !descriptor.runtime.primaryTool
  ) {
    throw new Error(
      `Workflow '${descriptor.id}' is facilitated and must define runtime.primaryTool.`,
    );
  }

  if (
    descriptor.output.summaryCapture?.createsPrimaryRecord &&
    descriptor.output.primaryRecord.routing.mode === "fixed" &&
    descriptor.output.summaryCapture.descriptionField === ""
  ) {
    throw new Error(
      `Workflow '${descriptor.id}' summaryCapture.descriptionField cannot be empty.`,
    );
  }
}

export function getWorkflowSummaryCaptureDefinition(
  descriptor: WorkflowDescriptor,
): {
  routing: WorkflowPrimaryRecordRouting;
  nameTemplate: string;
  descriptionField?: string;
  descriptionSource: WorkflowSummaryDescriptionSource;
} | null {
  const { summaryCapture } = descriptor.output;
  const { routing } = descriptor.output.primaryRecord;

  if (!summaryCapture || !summaryCapture.createsPrimaryRecord) {
    return null;
  }

  return {
    routing:
      routing.mode === "fixed"
        ? { mode: "fixed", collection: routing.collection }
        : { mode: "classifier" },
    nameTemplate: summaryCapture.nameTemplate,
    descriptionField: summaryCapture.descriptionField,
    descriptionSource: summaryCapture.descriptionSource ?? "final-output-text",
  };
}

export function getWorkflowPrimaryRecordCollection(
  descriptor: WorkflowDescriptor,
): string | null {
  const { routing } = descriptor.output.primaryRecord;
  return routing.mode === "fixed" ? routing.collection : null;
}

export function getWorkflowTemplateMetadata(
  descriptor: WorkflowDescriptor,
): { slug: string; name: string } {
  return {
    slug: descriptor.template.slug,
    name: descriptor.template.name,
  };
}

export function getWorkflowContextCollection(
  descriptor: WorkflowDescriptor,
): string | null {
  return getWorkflowPrimaryRecordCollection(descriptor);
}

export function describeWorkflowPrimaryOutput(
  descriptor: WorkflowDescriptor,
): string {
  const { routing } = descriptor.output.primaryRecord;

  if (routing.mode === "fixed") {
    return `Primary record routes to \`${routing.collection}\`.`;
  }

  return "Primary record is classifier-routed at runtime.";
}

export function compileWorkflowDefinition(
  descriptor: WorkflowDescriptor,
): WorkflowDefinition {
  validateWorkflowDescriptor(descriptor);
  const clonedDescriptor = cloneWorkflowDescriptor(descriptor);
  const primaryRecordCollection = getWorkflowPrimaryRecordCollection(
    clonedDescriptor,
  );
  const summaryCapture = getWorkflowSummaryCaptureDefinition(clonedDescriptor);

  return {
    id: clonedDescriptor.id,
    name: clonedDescriptor.name,
    shortDescription: clonedDescriptor.shortDescription,
    icon: clonedDescriptor.icon,
    facilitatorPreamble: clonedDescriptor.facilitatorPreamble,
    rounds: clonedDescriptor.rounds,
    kbOutputCollection: primaryRecordCollection ?? undefined,
    kbOutputTemplate:
      summaryCapture && summaryCapture.routing.mode === "fixed"
      ? {
          nameTemplate: summaryCapture.nameTemplate,
          descriptionField: summaryCapture.descriptionField ?? "description",
        }
      : undefined,
    errorRecovery: clonedDescriptor.errorRecovery,
  };
}
